//TODO : Refactor after ai
/**
 * Eingabemaske für „anlegen“ und „ändern“ – für jede Objektklasse aus dem
 * Modell erzeugt: zuerst die Felder in der Reihenfolge des Datenmodells,
 * dann die Einzelbeziehungen (Kunde, Straßenobjekt, Maßnahmenart …).
 * Pflichtfelder und Pflichtbeziehungen werden beim Speichern geprüft.
 */

import { useMemo, useState } from "react";
import { personaVon, useDatenStore } from "@app/store";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/components/atoms/dialog";
import { Button, InputField } from "@shared/components/widgets/ooux";
import {
  ausgehendeRelationen,
  datensaetze,
  heute,
  naechsteKennung,
  neueId,
  relationsTitel,
  type Aktion,
  type Datensatz,
  type Feld,
  type ObjektKlasse,
  type Wert,
} from "@shared/demo-data/viacid";
import { useTranslation } from "@shared/locales";
import { Auswahlfeld } from "./Auswahlfeld";

const TITELFELDER = [
  "name",
  "bezeichnung",
  "titel",
  "objektbezeichnung",
  "anlass",
  "mangelbeschreibung",
  "position",
];

function titelAus(
  klasse: ObjektKlasse,
  werte: Record<string, Wert>,
  kennung: string | null
): string {
  const key = TITELFELDER.find(
    (k) => typeof werte[k] === "string" && String(werte[k]).trim() !== ""
  );
  if (key) return String(werte[key]);
  return `${klasse.name} ${kennung ?? heute()}`;
}

function alsEingabe(wert: Wert | undefined): string {
  if (wert == null) return "";
  if (Array.isArray(wert)) return wert.join(", ");
  return String(wert);
}

function ausEingabe(feld: Feld, text: string): Wert | undefined {
  if (feld.typ === "Boolean") return text === "true";
  if (text.trim() === "") return undefined;
  if (feld.typ === "Ganzzahl") return Math.round(Number(text));
  if (feld.typ === "Dezimalzahl") return Number(text.replace(",", "."));
  if (feld.mehrfach)
    return text
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  return text;
}

const INPUT_TYP: Partial<Record<Feld["typ"], string>> = {
  Ganzzahl: "number",
  Dezimalzahl: "number",
  Datum: "date",
  "Datum und Uhrzeit": "datetime-local",
};

interface SatzFormularProps {
  klasse: ObjektKlasse;
  aktion: Aktion;
  /** Beim Ändern: der Datensatz. Beim Anlegen: leer. */
  satz?: Datensatz;
  /** Vorbelegte Beziehungen, z. B. die Aufnahme beim Erfassen einer Schadstelle. */
  vorbelegung?: Record<string, string>;
  onClose: (neuerSatz?: Datensatz) => void;
}

export function SatzFormular({
  klasse,
  aktion,
  satz,
  vorbelegung = {},
  onClose,
}: SatzFormularProps) {
  const { t } = useTranslation();
  const bestand = useDatenStore((s) => s.bestand);
  const rolle = useDatenStore((s) => s.rolle);
  const anlegen = useDatenStore((s) => s.anlegen);
  const aendern = useDatenStore((s) => s.aendern);

  const felder = klasse.felder.filter(
    (f) => f.key !== klasse.kennungFeld && f.typ !== "Datei"
  );
  const einzel = ausgehendeRelationen(klasse.slug).filter(
    (r) => !r.zuViele && r.zu !== klasse.slug
  );

  const [werte, setWerte] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      felder.map((f) => [
        f.key,
        satz
          ? alsEingabe(satz.werte[f.key])
          : f.typ === "Datum" && f.pflicht
            ? heute()
            : f.typ === "Boolean"
              ? "false"
              : "",
      ])
    )
  );
  const [bezuege, setBezuege] = useState<Record<string, string>>(() => {
    const persona = personaVon(rolle);
    return Object.fromEntries(
      einzel.map((r) => {
        const bestehend = satz?.bezuege[r.id];
        const vor =
          vorbelegung[r.id] ??
          (r.zu === "mitarbeiter" && !satz && r.kardinalitaetZu === "1"
            ? persona
            : undefined);
        return [
          r.id,
          (Array.isArray(bestehend) ? bestehend[0] : bestehend) ?? vor ?? "",
        ];
      })
    );
  });
  const [fehler, setFehler] = useState<Record<string, string>>({});

  // Vorschläge für Auswahlfelder aus den vorhandenen Werten – Freitext bleibt möglich
  const vorschlaege = useMemo(
    () =>
      Object.fromEntries(
        felder
          .filter((f) => f.typ === "Auswahl")
          .map((f) => [
            f.key,
            [
              ...new Set(
                datensaetze(bestand, klasse.slug).flatMap((d) => {
                  const w = d.werte[f.key];
                  return Array.isArray(w) ? w : w != null ? [String(w)] : [];
                })
              ),
            ].sort(),
          ])
      ),
    [bestand, felder, klasse.slug]
  );

  const optionen = useMemo(
    () =>
      Object.fromEntries(
        einzel.map((r) => [
          r.id,
          datensaetze(bestand, r.zu).map((d) => ({
            value: d.id,
            label: d.kennung ? `${d.kennung} · ${d.titel}` : d.titel,
          })),
        ])
      ),
    [bestand, einzel]
  );

  const speichern = () => {
    const f: Record<string, string> = {};
    for (const feld of felder)
      if (feld.pflicht && !werte[feld.key]?.trim())
        f[feld.key] = t("viacid.formular.pflicht");
    for (const r of einzel)
      if (r.kardinalitaetZu === "1" && !bezuege[r.id])
        f[r.id] = t("viacid.formular.pflicht");
    setFehler(f);
    if (Object.keys(f).length > 0) return;

    const neueWerte: Record<string, Wert> = {};
    for (const feld of felder) {
      const w = ausEingabe(feld, werte[feld.key] ?? "");
      if (w !== undefined) neueWerte[feld.key] = w;
    }
    const neueBezuege: Record<string, string | string[]> = {};
    for (const r of einzel)
      if (bezuege[r.id]) neueBezuege[r.id] = bezuege[r.id];

    if (satz) {
      aendern(
        {
          slug: klasse.slug,
          id: satz.id,
          werte: neueWerte,
          bezuege: neueBezuege,
        },
        t("viacid.formular.gespeichert", { aktion: aktion.name })
      );
      onClose();
      return;
    }
    const kennung = klasse.kennungFeld
      ? naechsteKennung(bestand, klasse.slug)
      : null;
    if (klasse.kennungFeld && kennung) neueWerte[klasse.kennungFeld] = kennung;
    const neu: Datensatz = {
      id: neueId(bestand, klasse.slug),
      titel: titelAus(klasse, neueWerte, kennung),
      untertitel: null,
      kennung,
      status: aktion.ziel ?? klasse.zustaende[0] ?? null,
      werte: neueWerte,
      bezuege: neueBezuege,
    };
    anlegen(
      klasse.slug,
      neu,
      t("viacid.formular.angelegt", { name: klasse.name, titel: neu.titel })
    );
    onClose(neu);
  };

  return (
    <Dialog open onOpenChange={(offen) => !offen && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{aktion.name}</DialogTitle>
          <DialogDescription>{klasse.beschreibung}</DialogDescription>
        </DialogHeader>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 16,
          }}
        >
          {felder.map((feld) =>
            feld.typ === "Boolean" ? (
              <Auswahlfeld
                key={feld.key}
                label={feld.label}
                value={werte[feld.key] ?? "false"}
                onChange={(v) => setWerte((w) => ({ ...w, [feld.key]: v }))}
                optionen={[
                  { value: "true", label: t("viacid.ja") },
                  { value: "false", label: t("viacid.nein") },
                ]}
              />
            ) : (
              <InputField
                key={feld.key}
                id={`feld-${feld.key}`}
                label={feld.label}
                required={feld.pflicht}
                type={INPUT_TYP[feld.typ] ?? "text"}
                step={feld.typ === "Dezimalzahl" ? "any" : undefined}
                value={werte[feld.key] ?? ""}
                onChange={(e) =>
                  setWerte((w) => ({ ...w, [feld.key]: e.target.value }))
                }
                error={fehler[feld.key]}
                helper={
                  feld.mehrfach ? t("viacid.formular.mehrfach") : undefined
                }
                list={
                  vorschlaege[feld.key] ? `vorschlag-${feld.key}` : undefined
                }
              />
            )
          )}
          {einzel.map((r) => (
            <Auswahlfeld
              key={r.id}
              label={relationsTitel(r, "ausgehend")}
              required={r.kardinalitaetZu === "1"}
              value={bezuege[r.id] ?? ""}
              onChange={(v) => setBezuege((b) => ({ ...b, [r.id]: v }))}
              optionen={[{ value: "", label: "—" }, ...(optionen[r.id] ?? [])]}
              error={fehler[r.id]}
            />
          ))}
        </div>
        {Object.entries(vorschlaege).map(([key, werteListe]) => (
          <datalist key={key} id={`vorschlag-${key}`}>
            {werteListe.map((w) => (
              <option key={w} value={w} />
            ))}
          </datalist>
        ))}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="outline" color="neutral" onClick={() => onClose()}>
            {t("viacid.abbrechen")}
          </Button>
          <Button onClick={speichern}>
            {satz ? t("viacid.speichern") : aktion.name}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
